<script lang="ts" setup>
import { type MusicRank, type MusicCategory } from '~/services/musicService';
import { getSectionById } from '~/services/musicSectionMapConfig';
import { useI18n } from 'vue-i18n';
import { useRouter, useNuxtApp, ref, onMounted, onActivated, onUnmounted, onBeforeRouteLeave, watch } from '#imports';
import { useSeoMeta, useHead } from '#imports';
import SearchBox from '~/components/music/SearchBox.vue';
import CloudflareImg from '~/components/base/CloudflareImg.vue';
import { useMusicCache } from '~/composables/useMusicCache';
import { useHomeMusicSection } from '~/composables/useHomeMusicSection';

const { t, locale } = useI18n();
const router = useRouter();
const nuxtApp = useNuxtApp();
const musicCache = useMusicCache();

// Carry public SSR data in the Nuxt payload. Hydration must keep the rendered
// lists instead of replacing them with skeletons and fetching them again.
const initialHomeData = useState<{
  locale: string;
  trending: MusicRank[];
  categories: MusicCategory[];
  expiresAt: number;
} | null>('home-music-data', () => null);
const hasInitialData = initialHomeData.value?.locale === locale.value && initialHomeData.value.expiresAt > Date.now();
const trendingSection = useHomeMusicSection<MusicRank>(
  musicCache.getFeaturedMusic, () => locale.value,
  hasInitialData ? initialHomeData.value!.trending : undefined,
);
const categoriesSection = useHomeMusicSection<MusicCategory>(
  musicCache.getCategoryList, () => locale.value,
  hasInitialData ? initialHomeData.value!.categories : undefined,
);
const { list: musicTrending, isLoading: isLoadingTrending, error: trendingError } = trendingSection;
const { list: musicCategories, isLoading: isLoadingCategories, error: categoriesError } = categoriesSection;

// 播放相关功能
const currentPlayingId = ref<string | null>(null);
const isPlaying = ref(false);
const isAudioLoading = ref(false);

// The same charts are a grid on wide screens and a native scroll rail on small
// screens. Arrow controls supplement touch scrolling; they never change data.
const chartRail = ref<HTMLElement | null>(null);
const chartPositions = useState<Record<string, number>>('home-chart-positions', () => ({}));
const chartScroll = ref({ canScroll: false, canPrevious: false, canNext: false });
let chartResizeObserver: ResizeObserver | undefined;
let chartRestorePending = false;
const updateChartScroll = () => {
  const rail = chartRail.value;
  const maximum = rail ? rail.scrollWidth - rail.clientWidth : 0;
  // Nuxt's async page can set template refs while its Suspense subtree is still
  // detached. Wait for measurable, connected layout before restoring position.
  if (rail?.isConnected && rail.clientWidth > 0 && chartRestorePending) {
    chartRestorePending = false;
    const saved = chartPositions.value?.[locale.value];
    rail.scrollLeft = typeof saved === 'number' && Number.isFinite(saved) && saved >= 0
      ? Math.min(Math.max(0, maximum), saved) : 0;
  }
  const next = {
    canScroll: maximum > 1,
    canPrevious: Boolean(rail && rail.scrollLeft > 1),
    canNext: Boolean(rail && rail.scrollLeft < maximum - 1),
  };
  if (next.canScroll !== chartScroll.value.canScroll || next.canPrevious !== chartScroll.value.canPrevious || next.canNext !== chartScroll.value.canNext) {
    chartScroll.value = next;
  }
};
const rememberChartScroll = () => {
  updateChartScroll();
  if (chartRail.value && chartScroll.value.canScroll) {
    chartPositions.value[locale.value] = Math.max(0, chartRail.value.scrollLeft);
  }
};
// Capture at navigation, not on every scroll: mounting a returning page also
// emits an initial zero-position scroll, which must not overwrite the snapshot.
onBeforeRouteLeave(rememberChartScroll);
const scrollChart = (direction: -1 | 1) => {
  const rail = chartRail.value;
  if (!rail) return;
  const cards = Array.from(rail.querySelectorAll<HTMLElement>('[data-ranking-id]'));
  if (!cards.length) return;
  const start = cards[0].getBoundingClientRect().left;
  const positions = cards.map(card => card.getBoundingClientRect().left - start);
  const current = positions.reduce((nearest, position, index) =>
    Math.abs(position - rail.scrollLeft) < Math.abs(positions[nearest] - rail.scrollLeft) ? index : nearest, 0);
  const target = Math.max(0, Math.min(cards.length - 1, current + direction));
  rail.scrollTo({
    left: positions[target],
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });
};
watch(chartRail, rail => {
  if (!import.meta.client) return;
  chartResizeObserver?.disconnect();
  chartRestorePending = Boolean(rail);
  if (rail) {
    chartResizeObserver = new ResizeObserver(updateChartScroll);
    chartResizeObserver.observe(rail);
  }
  updateChartScroll();
}, { flush: 'post' });
watch(musicTrending, updateChartScroll, { flush: 'post' });
// KeepAlive reuses this ref when returning; detached scroll containers lose
// their position even though the component itself has not mounted again.
onActivated(() => {
  chartRestorePending = true;
  updateChartScroll();
});
const stopChartPageFinish = nuxtApp.hook('page:finish', updateChartScroll);
const stopChartTransitionFinish = nuxtApp.hook('page:transition:finish', updateChartScroll);

// 检查是否正在播放特定音乐
const isCurrentlyPlaying = (id: string) => {
  return currentPlayingId.value === id && isPlaying.value;
};
const isCurrentlyLoading = (id: string) => currentPlayingId.value === id && isAudioLoading.value;

// 播放音乐
const playMusic = (music: any) => {
  // 如果点击当前播放的音乐，则切换播放状态
  if (currentPlayingId.value === music.id) {
    togglePlayState();
  } else {
    // 否则播放新的音乐
    currentPlayingId.value = music.id;

    // 触发全局事件，通知音乐播放器组件
    const event = new CustomEvent('play-music', {
      detail: music
    });
    window.dispatchEvent(event);
  }
};

// 切换播放状态
const togglePlayState = () => {
  const shouldPlay = !isPlaying.value && !isAudioLoading.value;

  // 触发全局事件，通知音乐播放器组件
  const event = new CustomEvent('toggle-play-state', {
    detail: {
      id: currentPlayingId.value,
      isPlaying: shouldPlay
    }
  });
  window.dispatchEvent(event);
};

// Keep successful sections independent; a retry must not reload its neighbour.
const cacheHomeData = () => {
  if (trendingError.value || categoriesError.value || isLoadingTrending.value || isLoadingCategories.value) return;
  if (trendingSection.loadedKey.value !== locale.value || categoriesSection.loadedKey.value !== locale.value) return;
  initialHomeData.value = { locale: locale.value, trending: musicTrending.value, categories: musicCategories.value, expiresAt: Date.now() + 3600000 };
};
const loadTrending = async () => { await trendingSection.load(); cacheHomeData(); };
const loadCategories = async () => { await categoriesSection.load(); cacheHomeData(); };
const loadData = () => Promise.all([loadTrending(), loadCategories()]);

// SSR waits within the existing cache budget; SPA navigation remains immediate.
if (import.meta.server) await loadData();
else if (!hasInitialData) void loadData();
watch(locale, () => { void loadData(); });

const handlePlaybackState = ((event: CustomEvent) => {
  currentPlayingId.value = event.detail.id;
  isPlaying.value = Boolean(event.detail.isPlaying);
  isAudioLoading.value = Boolean(event.detail.isLoading);
}) as EventListener;
onMounted(() => {
  window.addEventListener('playing-state-changed', handlePlaybackState);
  window.dispatchEvent(new Event('request-music-player-state'));
  // A transient SSR failure gets one client fallback; empty success is not a failure.
  if (trendingError.value) void loadTrending();
  if (categoriesError.value) void loadCategories();
});
onUnmounted(() => {
  chartResizeObserver?.disconnect();
  stopChartPageFinish();
  stopChartTransitionFinish();
  window.removeEventListener('playing-state-changed', handlePlaybackState);
});

// SEO配置 - 使用新的composable
const { setHomePageSeo } = useSeo();
setHomePageSeo();

// FAQPage 结构化数据
const faqSchema = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: t('music.faqList.q1'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a1')
      }
    },
    {
      '@type': 'Question',
      name: t('music.faqList.q2'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a2')
      }
    },
    {
      '@type': 'Question',
      name: t('music.faqList.q3'),
      acceptedAnswer: {
        '@type': 'Answer',
        text: t('music.faqList.a3')
      }
    }
  ]
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      children: () => JSON.stringify(faqSchema.value)
    }
  ]
});

const searchQuery = ref('');
const discoveryLinks = [
  ['ai-birthday-song-generator', 'birthday'],
  ['ai-love-song-generator', 'love'],
  ['ai-rap-song-generator', 'rap'],
  ['ai-pop-song-generator', 'pop'],
  ['ai-anime-song-generator', 'anime'],
  ['youtube-intro-music-generator', 'youtube'],
  ['podcast-intro-music-generator', 'podcast'],
  ['royalty-free-ai-music-generator', 'royaltyFree'],
];

// FAQ 交互状态
const activeFaqIndex = ref<number | null>(null);
const toggleFaq = (index: number) => {
  activeFaqIndex.value = activeFaqIndex.value === index ? null : index;
};
</script>

<template>
  <div class="h-auto text-gray-100 relative overflow-hidden">
    <!-- 主容器 -->
    <div class="lg:ml-4 lg:pr-4 relative">
      <!-- 页面顶部合适的位置添加搜索框 -->
      <div class="w-full max-w-3xl mx-auto mb-4 md:mb-8">
        <SearchBox
          v-model="searchQuery"
          :show-cancel="false"
          @submit="query => router.push({ path: '/music/search', query: { q: query } })"
        />
      </div>

      <!-- 移动端金刚位导航 -->
      <div class="grid grid-cols-4 gap-2 mb-6 md:hidden">
        <!-- 音乐生成 -->
        <NuxtLink to="/create"
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-music text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.createMusicShort') }}</span>
        </NuxtLink>

        <!-- 歌词生成 -->
        <NuxtLink to="/lyrics-generator"
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-file-text text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.lyricGeneratorShort') }}</span>
        </NuxtLink>

        <!-- 音乐库 -->
        <NuxtLink to="/library"
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-library text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('nav.library') }}</span>
        </NuxtLink>

        <!-- 其他 -->
        <NuxtLink to="/m-create"
          class="flex flex-col items-center justify-center cursor-pointer gap-1"
        >
          <div class="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-purple-600 dark:text-white">
            <div class="i-lucide-grid-3x3 text-2xl"></div>
          </div>
          <span class="text-xs text-gray-300">{{ t('common.more') || '其他' }}</span>
        </NuxtLink>
      </div>

      <!-- 页面标题 -->
      <!-- The create action sits beside the title so it adds no row of its own. -->
      <div class="md:mb-8 md:flex md:items-end md:justify-between md:gap-6">
        <div class="min-w-0">
          <h1 class="sr-only md:not-sr-only text-3xl md:text-4xl font-bold mb-2 text-white">{{ t('music.homeH1') }}</h1>
          <p class="hidden md:block text-gray-400 text-lg">{{ t('music.discoverSubtitle') }}</p>
        </div>
        <NuxtLink to="/create" class="musicmint-primary-button hidden md:inline-flex shrink-0 whitespace-nowrap">{{ t('nav.createMusicShort') }}<span class="i-lucide-arrow-right" aria-hidden="true" /></NuxtLink>
      </div>

      <!-- One chart set for mouse, keyboard and touch. -->
      <section class="home-charts-section" aria-labelledby="home-trending-heading" :aria-busy="isLoadingTrending">
        <div class="home-charts-heading">
          <h2 id="home-trending-heading">{{ t('music.musicTrending') }}</h2>
          <div v-if="!isLoadingTrending && musicTrending.length > 1" class="home-chart-controls">
            <button type="button" :disabled="!chartScroll.canPrevious" :aria-label="t('ux.homePreviousChart')" aria-controls="home-charts-list" @click="scrollChart(-1)">
              <span class="i-lucide-chevron-left h-5 w-5" aria-hidden="true" />
            </button>
            <button type="button" :disabled="!chartScroll.canNext" :aria-label="t('ux.homeNextChart')" aria-controls="home-charts-list" @click="scrollChart(1)">
              <span class="i-lucide-chevron-right h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <p v-if="isLoadingTrending" role="status" class="sr-only">{{ t('music.loading') }}</p>
        <div v-if="trendingError" role="alert" class="home-section-feedback">
          <p>{{ t('ux.homeTrendingUnavailable') }}</p>
          <button type="button" class="musicmint-secondary-button" @click="loadTrending">{{ t('ux.retry') }}</button>
        </div>
        <p v-else-if="!isLoadingTrending && !musicTrending.length" role="status" class="home-section-empty">{{ t('ux.homeTrendingEmpty') }}</p>

        <div v-if="isLoadingTrending" class="home-charts-grid home-charts-skeleton" aria-hidden="true">
          <div v-for="i in 3" :key="i" class="home-chart">
            <div class="home-chart-header"><div class="shimmer-title h-6 w-1/2 rounded" /></div>
            <div v-for="j in 3" :key="j" class="home-chart-track">
              <div class="shimmer-text h-4 w-3 rounded" />
              <div class="home-chart-song">
                <div class="home-chart-art shimmer-square" />
                <div class="home-chart-song-copy"><div class="shimmer-line h-4 w-full rounded mb-2" /><div class="shimmer-line h-3 w-2/3 rounded" /></div>
              </div>
              <div class="shimmer-circle h-11 w-11 rounded-full" />
            </div>
          </div>
        </div>
        <div v-else-if="musicTrending.length" id="home-charts-list" ref="chartRail" class="home-charts-grid" role="region" :aria-label="t('music.musicTrending')" :tabindex="chartScroll.canScroll ? 0 : undefined" @scroll.passive="updateChartScroll">
          <section v-for="trending in musicTrending" :key="trending.id" :data-ranking-id="trending.id" :aria-labelledby="`home-chart-title-${trending.id}`" class="home-chart">
            <div class="home-chart-header">
              <h3 :id="`home-chart-title-${trending.id}`">{{ trending.name }}</h3>
              <NuxtLink
                :to="getSectionById(trending.section_id, 'rank', trending.selected_option)
                  ? `/music/${getSectionById(trending.section_id, 'rank', trending.selected_option)?.path}`
                  : `/music?chart=${trending.section_id}&title=${trending.name}`"
                :title="`${t('music.trendingSubtitle')}: ${trending.name}`"
                :aria-label="`${t('music.trendingSubtitle')}: ${trending.name}`"
                @click="rememberChartScroll"
                class="home-chart-more">
                {{ t('music.more') }}
              </NuxtLink>
            </div>
            <ol class="home-chart-tracks">
              <li v-for="(music, index) in trending.top_songs" :key="music.id" class="home-chart-track" :class="{ 'home-chart-track-playing': isCurrentlyPlaying(music.id) }">
                <span class="home-chart-rank" aria-hidden="true">{{ index + 1 }}</span>
                <NuxtLink :to="`/music/song/${music.id}`" :aria-label="`${music.title} — ${music.display_name}`" class="home-chart-song" @click="rememberChartScroll">
                  <div class="home-chart-art">
                    <CloudflareImg :src="music.image_url" alt="" :width="128" :height="128" quality="80" rounded="md" />
                    <div v-if="isCurrentlyPlaying(music.id)" class="home-chart-playing" aria-hidden="true">
                      <div class="sound-wave-small"><span /><span /><span /></div>
                    </div>
                  </div>
                  <div class="home-chart-song-copy">
                    <h4>{{ music.title }}</h4>
                    <p>{{ music.display_name }}</p>
                  </div>
                </NuxtLink>
                <button @click.stop="playMusic(music)" type="button" :aria-pressed="isCurrentlyPlaying(music.id)" :aria-busy="isCurrentlyLoading(music.id)"
                  :aria-label="t(isCurrentlyLoading(music.id) ? 'ux.cancelPlayback' : isCurrentlyPlaying(music.id) ? 'music.pause' : 'music.play') + ' ' + music.title"
                  class="home-chart-play">
                  <i v-if="isCurrentlyLoading(music.id)" class="i-lucide-loader-2 h-5 w-5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  <svg v-else-if="isCurrentlyPlaying(music.id)" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
                </button>
              </li>
            </ol>
          </section>
        </div>
      </section>

      <!-- 音乐分类部分 -->
      <section aria-labelledby="home-categories-heading" :aria-busy="isLoadingCategories">
        <div class="flex justify-between items-center mb-6">
          <h2 id="home-categories-heading" class="text-2xl font-bold text-white">{{ t('music.musicCategories') }}</h2>
        </div>

        <p v-if="isLoadingCategories" role="status" class="sr-only">{{ t('music.loading') }}</p>
        <div v-if="categoriesError" role="alert" class="home-section-feedback">
          <p>{{ t('ux.homeCategoriesUnavailable') }}</p>
          <button type="button" class="musicmint-secondary-button" @click="loadCategories">{{ t('ux.retry') }}</button>
        </div>
        <p v-else-if="!isLoadingCategories && !musicCategories.length" role="status" class="home-section-empty">{{ t('ux.homeCategoriesEmpty') }}</p>

        <!-- 移动端分类加载状态 -->
        <div v-if="isLoadingCategories" class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 md:gap-6">
          <!-- Shimmer 效果 -->
          <div
            v-for="i in 4"
            :key="i"
            class="relative rounded-xl overflow-hidden aspect-square shadow-lg bg-gray-800/40 group transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <div class="shimmer-image absolute inset-0"></div>
            <!-- 渐变遮罩 -->
            <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent z-20"></div>
            <!-- 分类信息 -->
            <div class="absolute bottom-0 left-0 right-0 p-4 z-30">
              <div class="shimmer-title h-6 w-3/4 rounded-md"></div>
            </div>
          </div>
        </div>

        <!-- 移动端分类内容 -->
        <div v-else-if="musicCategories.length" class="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-6 md:gap-6">
          <NuxtLink
            v-for="(category, index) in musicCategories"
            :key="category.id"
            :to="`/music/${category.search_tag}`"
            :title="`${t('music.exploreCategory')}: ${category.name}`"
            :aria-label="`${t('music.exploreCategory')}: ${category.name}`"
            :data-category-id="category.id"
            class="category-card group relative rounded-xl overflow-hidden aspect-square shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105"
          >
            <!-- 分类封面 -->
              <CloudflareImg
                :src="category.image_url"
                :alt="category.name"
                rounded="xl"
                :loading="index < 4 ? 'eager' : 'lazy'"
                :showPreload="false"
                width="128"
                height="128"
                quality="80"
                class="transition-transform duration-500 group-hover:scale-110"
              />

            <!-- 渐变遮罩 -->
            <div class="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-transparent z-20"></div>

            <!-- 分类信息 -->
            <div class="absolute bottom-0 left-0 right-0 p-4 z-30">
              <h3 class="text-xl font-bold text-white mb-1 group-hover:text-blue-300 transition-colors">
                {{ category.name }}
              </h3>
            </div>
          </NuxtLink>
        </div>
      </section>

      <section class="mt-8" aria-labelledby="home-discovery-heading">
        <div class="home-discovery-heading">
          <h2 id="home-discovery-heading" class="text-2xl font-bold text-white">{{ t('ux.homeDiscovery.heading') }}</h2>
          <NuxtLink to="/discover" class="home-discovery-all">
            {{ t('ux.homeDiscovery.viewAll') }}<span class="i-lucide-arrow-right h-4 w-4 shrink-0" aria-hidden="true" />
          </NuxtLink>
        </div>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
          <NuxtLink v-for="[slug, label] in discoveryLinks" :key="slug" :to="`/discover/${slug}`" class="home-discovery-link"><MusicDiscoveryIcon :topic="label" /><span>{{ t(`ux.homeDiscovery.${label}`) }}</span></NuxtLink>
        </div>
      </section>

      <!-- SEO Content Sections: Features & FAQ -->
      <div class="mt-8 lg:mt-16 mb-12 space-y-12 lg:space-y-24">
        <!-- Features Section -->
        <section>
          <div class="text-center mb-10">
            <h2 class="text-3xl font-bold text-white mb-4">{{ t('music.sections.features') }}</h2>
            <div class="w-20 h-1 bg-blue-500 mx-auto rounded-full"></div>
          </div>

          <div class="grid md:grid-cols-3 gap-6">
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-blue-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-blue-400 mb-3">{{ t('music.featureList.textToMusic.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.textToMusic.desc') }}</p>
            </div>
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-purple-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-purple-400 mb-3">{{ t('music.featureList.lyricsGenerator.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.lyricsGenerator.desc') }}</p>
            </div>
            <div class="bg-gray-800/40 p-6 rounded-xl border border-gray-700/50 hover:border-teal-500/30 transition-colors duration-300">
              <h3 class="text-xl font-semibold text-teal-400 mb-3">{{ t('music.featureList.vocalSeparation.title') }}</h3>
              <p class="text-gray-300 leading-relaxed">{{ t('music.featureList.vocalSeparation.desc') }}</p>
            </div>
          </div>
        </section>

        <!-- FAQ Section -->
        <section>
          <div class="text-center mb-10">
            <h2 class="text-3xl font-bold text-white mb-4">{{ t('music.sections.faq') }}</h2>
            <p class="text-gray-400 text-lg leading-relaxed mb-6">{{ t('music.sections.faqSubtitle') }}</p>
             <div class="w-20 h-1 bg-purple-500 mx-auto rounded-full"></div>
          </div>

          <div class="grid md:grid-cols-3 gap-6">
            <div
              v-for="i in 3"
              :key="i"
              class="bg-gray-800/40 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden transition-all duration-300 hover:border-blue-500/30 h-fit"
            >
              <button
                type="button"
                :aria-expanded="activeFaqIndex === i"
                :aria-controls="`home-faq-answer-${i}`"
                @click="toggleFaq(i)"
                class="w-full flex items-center justify-between p-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-300 group"
              >
                <h3 class="font-semibold text-lg text-white group-hover:text-blue-300 transition-colors">{{ t(`music.faqList.q${i}`) }}</h3>
                <span
                  class="transform transition-transform duration-300 text-gray-400 group-hover:text-blue-300"
                  :class="{ 'rotate-180': activeFaqIndex === i }"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </span>
              </button>
              <div
                :id="`home-faq-answer-${i}`"
                v-show="activeFaqIndex === i"
                class="px-5 pb-5 text-gray-300 border-t border-gray-700/30 pt-3"
              >
                <p class="leading-relaxed">{{ t(`music.faqList.a${i}`) }}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.home-discovery-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
.home-discovery-heading h2 { min-width: 0; overflow-wrap: anywhere; line-height: 1.35; }
.home-discovery-all { display: inline-flex; flex-shrink: 0; align-items: center; justify-content: center; gap: 6px; min-height: 44px; color: #93c5fd; font-size: 14px; }
.home-discovery-link { display: flex; min-width: 0; align-items: center; gap: 12px; min-height: 76px; padding: 12px; border: 1px solid var(--mint-border); border-radius: 12px; background: var(--mint-surface); color: var(--mint-ink); font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; }
.home-discovery-link > span:last-child { min-width: 0; }
.home-discovery-link:hover { border-color: #60a5fa; background: var(--mint-surface-raised); }
.home-discovery-link:focus-visible, .home-discovery-all:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: -2px; }
.home-section-feedback { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 16px; border: 1px solid #f8717140; border-radius: 12px; background: #ef444408; color: #fecaca; }
.home-section-feedback p { flex: 1 1 200px; min-width: 0; font-size: 14px; line-height: 1.6; }
.home-section-feedback button { flex-shrink: 0; }
.home-section-empty { padding-block: 24px; color: var(--mint-muted); font-size: 14px; line-height: 1.6; }

/* 添加shimmer效果样式 */
.shimmer-title,
.shimmer-line,
.shimmer-circle,
.shimmer-square,
.shimmer-text,
.shimmer-image {
  @apply bg-gradient-to-r from-gray-700/50 via-gray-600/50 to-gray-700/50 bg-[length:400%_100%];
  animation: shimmer 2s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* Real artwork, then title, then the playback action. No chart card surface. */
.home-charts-section { margin-bottom: 36px; }
.home-charts-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.home-charts-heading h2 { font-size: 24px; font-weight: 700; line-height: 1.35; color: var(--mint-ink); }
.home-chart-controls { display: flex; flex-shrink: 0; gap: 4px; }
.home-chart-controls button { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border: 1px solid var(--mint-border); border-radius: 50%; color: var(--mint-ink); background: transparent; }
.home-chart-controls button:hover:not(:disabled) { background: var(--mint-surface-raised); }
.home-chart-controls button:disabled { opacity: .35; cursor: default; }
.home-charts-grid { display: flex; position: relative; gap: 16px; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: thin; scrollbar-color: var(--mint-border) transparent; padding-bottom: 8px; }
.home-chart { flex: 0 0 min(360px, 88%); min-width: 0; scroll-snap-align: start; border-bottom: 1px solid var(--mint-border); }
.home-chart:only-child { flex-basis: 100%; }
.home-chart-header { display: flex; align-items: center; justify-content: space-between; min-height: 52px; gap: 12px; border-bottom: 1px solid var(--mint-border); }
.home-chart-header h3 { min-width: 0; overflow-wrap: anywhere; font-size: 20px; line-height: 1.35; font-weight: 650; color: var(--mint-ink); }
.home-chart-more { display: inline-flex; align-items: center; flex-shrink: 0; min-height: 44px; color: #93c5fd; font-size: 13px; }
.home-chart-more:hover { text-decoration: underline; text-underline-offset: 4px; }
.home-chart-tracks { margin: 0; padding: 0; list-style: none; }
.home-chart-track { display: grid; grid-template-columns: 16px minmax(0, 1fr) 44px; align-items: center; gap: 10px; padding: 12px 0; }
.home-chart-rank { font-size: 14px; font-weight: 500; color: var(--mint-muted); font-variant-numeric: tabular-nums; text-align: center; }
.home-chart-song { display: flex; align-items: center; gap: 12px; min-width: 0; border-radius: 6px; }
.home-chart-art { flex-shrink: 0; position: relative; width: 56px; height: 56px; overflow: hidden; border-radius: 6px; }
.home-chart-song-copy { min-width: 0; flex: 1; }
.home-chart-song h4 { margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 15px; font-weight: 550; line-height: 1.4; color: var(--mint-ink); }
.home-chart-song p { margin-top: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; line-height: 1.4; color: var(--mint-muted); }
.home-chart-song:hover h4 { color: #93c5fd; }
.home-chart-play { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; border: 1px solid var(--mint-border); border-radius: 50%; background: transparent; color: var(--mint-ink); transition: background-color 160ms, color 160ms; }
.home-chart-play:hover { background: var(--mint-surface-raised); }
.home-chart-track-playing .home-chart-play { background: #2563eb; color: #fff; border-color: #2563eb; }
.home-chart-playing { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: #0008; }
.home-chart-play:focus-visible, .home-chart-song:focus-visible, .home-chart-more:focus-visible, .home-chart-controls button:focus-visible, .home-charts-grid:focus-visible { outline: 2px solid var(--mint-focus); outline-offset: -2px; }
.home-charts-skeleton { pointer-events: none; }
.sound-wave-small { display: flex; align-items: center; height: 16px; gap: 3px; }
.sound-wave-small span { width: 3px; height: 12px; border-radius: 2px; background: white; animation: home-sound-wave 1.2s infinite ease-in-out; }
.sound-wave-small span:nth-child(2) { animation-delay: .2s; }
.sound-wave-small span:nth-child(3) { animation-delay: .4s; }
@keyframes home-sound-wave { 0%, 100% { transform: scaleY(.5); } 50% { transform: scaleY(1); } }
/* Trailing space lets the last chart snap to the start instead of stopping
   with the previous chart's edge still showing on the left. */
@media (max-width: 639px) {
  .home-charts-grid::after { content: ''; flex: 0 0 calc(100% - min(360px, 88%) - 16px); }
  .home-charts-grid:has(> .home-chart:only-child)::after { content: none; }
}
@media (min-width: 640px) and (max-width: 1279px) {
  .home-chart { flex-basis: calc((100% - 16px) / 2); }
}
@media (min-width: 1280px) {
  .home-charts-section { margin-bottom: 48px; }
  .home-charts-heading { margin-bottom: 16px; }
  .home-chart-controls { display: none; }
  .home-charts-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); overflow: visible; padding-bottom: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .home-chart-play { transition: none; }
  .sound-wave-small span, .home-charts-skeleton [class*="shimmer-"] { animation: none; }
}
</style>
